import { useEffect, useState } from "react";
import apiService from "../services/api";

export type PermissionStatus = "accepted" | "rejected" | "expired" | "unknown" | "requested";

export interface CallPermission {
  status: PermissionStatus;
  valid_until: string | null;
  requested_at: string | null;
}

/**
 * Stored WhatsApp call-permission state for the number about to be dialed.
 *
 * Owned by the calling modal rather than by the hint that displays it, because
 * two things now depend on it: the hint, and the default call method (an
 * already-approved patient should default to WhatsApp). Fetching it in one
 * place keeps that to a single request and one source of truth.
 *
 * A failed lookup resolves to "unknown" rather than staying null: not knowing
 * and knowing there is no approval lead to the same action - offer to send the
 * permission request - and leaving it null would hide the hint entirely.
 */
export function useWhatsAppCallPermission(params: {
  token: string;
  patientId: number | string;
  phone?: string;
}) {
  const { token, patientId, phone } = params;
  const [permission, setPermission] = useState<CallPermission | null>(null);

  useEffect(() => {
    if (!phone) {
      setPermission(null);
      return;
    }
    let stale = false;
    setPermission(null); // still loading - don't report the previous number's state
    apiService
      .getWhatsAppCallPermissionStatus({ token, patient_id: Number(patientId), phone })
      .then((res) => {
        if (stale) return;
        setPermission({ status: res.status, valid_until: res.valid_until, requested_at: res.requested_at });
      })
      .catch(() => {
        if (!stale) setPermission({ status: "unknown", valid_until: null, requested_at: null });
      });
    return () => {
      stale = true;
    };
  }, [token, patientId, phone]);

  return { permission, setPermission, approved: permission?.status === "accepted" };
}

export default useWhatsAppCallPermission;
